import React, { useMemo, useState, useEffect } from "react";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
const formatAmount = (amount) => {
  if (amount === null || amount === undefined) {
    return "-";
  }

  return new Intl.NumberFormat("fa-IR").format(Number(amount));
};

const formatDate = (date) => {
  if (!date) return "-";

  return new Intl.DateTimeFormat("fa-IR", {
    calendar: "persian",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(date));
};

const transactionTypes = [
  {
    value: "",
    label: "همه",
  },
  {
    value: "deposit",
    label: "شارژ کیف‌پول",
  },
  {
    value: "usage",
    label: "مصرف اعتبار",
  },
  {
    value: "settlementRequest",
    label: "درخواست تسویه",
  },
  {
    value: "settlementApproved",
    label: "تسویه تایید شده",
  },
  {
    value: "settlementRejected",
    label: "تسویه رد شده",
  },
];

const getTypeLabel = (type) => {
  const item = transactionTypes.find((item) => item.value === type);

  return item?.label || type || "-";
};

export default function FinancialLogsTable({ financialLogs = [] }) {
  const [typeFilter, setTypeFilter] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // تعداد آیتم در هر صفحه
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // صفحه فعلی
  const [currentPage, setCurrentPage] = useState(1);

  // ----------------------------------
  // Filter
  // ----------------------------------

  const filteredLogs = useMemo(() => {
    return financialLogs.filter((item) => {
      // فیلتر نوع
      if (typeFilter && item.type !== typeFilter) {
        return false;
      }

      // اگر تاریخ نداشت
      if (!item.createdAt) {
        return false;
      }

      const itemDate = new Date(item.createdAt);

      if (fromDate) {
        const startDate = fromDate.toDate();

        startDate.setHours(0, 0, 0, 0);

        if (itemDate < startDate) {
          return false;
        }
      }

      if (toDate) {
        const endDate = toDate.toDate();

        endDate.setHours(23, 59, 59, 999);

        if (itemDate > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [financialLogs, typeFilter, fromDate, toDate]);

  // ----------------------------------
  // Reset page when filters change
  // ----------------------------------

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, fromDate, toDate, itemsPerPage]);

  // ----------------------------------
  // Pagination
  // ----------------------------------

  const totalItems = filteredLogs.length;

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + itemsPerPage;

  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  // ----------------------------------
  // Totals
  // روی کل نتایج فیلترشده
  // ----------------------------------

  const totals = useMemo(() => {
    return filteredLogs.reduce(
      (acc, item) => {
        acc.amount += Number(item.amount || 0);
        acc.commissionAmount += Number(
          item.commissionAmount || 0
        );
        acc.taxAmount += Number(item.taxAmount || 0);
        acc.netAmount += Number(item.netAmount || 0);

        return acc;
      },
      {
        amount: 0,
        commissionAmount: 0,
        taxAmount: 0,
        netAmount: 0,
      }
    );
  }, [filteredLogs]);

  // ----------------------------------
  // Clear filters
  // ----------------------------------

  const clearFilters = () => {
    setTypeFilter("");
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  // ----------------------------------
  // Change page
  // ----------------------------------

  const changePage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    setCurrentPage(page);
  };

  // ----------------------------------
  // Pagination numbers
  // ----------------------------------

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 4) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 3) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="w-full overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">

      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800">
          گزارش درآمد حاصل از کارمزد
        </h2>

        <p className="text-sm text-gray-500 mt-1">
          گزارش مبالغ تراکنش‌ها و درآمد حاصل از کارمزد
        </p>
      </div>

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              نوع تراکنش
            </label>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              {transactionTypes.map((item) => (
                <option
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-4">
            {/* From Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                از تاریخ
              </label>

              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                format="YYYY/MM/DD"
                inputClass="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="از تاریخ"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                تا تاریخ
              </label>

              <DatePicker
                value={toDate}
                onChange={setToDate}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                format="YYYY/MM/DD"
                inputClass="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="تا تاریخ"
              />
            </div>
            {/* Clear */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                حذف فیلترها
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 border-b">

        <div className="grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-4 gap-4">

          {/* Total Amount */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
            <div className="text-sm text-gray-500">
              مجموع مبلغ تراکنش‌ها
            </div>

            <div className="mt-2 text-lg font-bold text-gray-800">
              {formatAmount(totals.amount)}
              <span className="text-xs font-normal mr-1">
                ریال
              </span>
            </div>
          </div>

          {/* Total Commission */}
          <div className="rounded-xl bg-green-50 border border-green-200 p-4">
            <div className="text-sm text-gray-500">
              مجموع کارمزد
            </div>

            <div className="mt-2 text-lg font-bold text-green-600">
              {formatAmount(totals.commissionAmount)}
              <span className="text-xs font-normal mr-1">
                ریال
              </span>
            </div>
          </div>

          {/* Total Tax */}
          <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4">
            <div className="text-sm text-gray-500">
              مجموع مالیات
            </div>

            <div className="mt-2 text-lg font-bold text-yellow-600">
              {formatAmount(totals.taxAmount)}
              <span className="text-xs font-normal mr-1">
                ریال
              </span>
            </div>
          </div>

          {/* Total Net */}
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
            <div className="text-sm text-gray-500">
              مجموع مبلغ خالص
            </div>

            <div className="mt-2 text-lg font-bold text-blue-600">
              {formatAmount(totals.netAmount)}
              <span className="text-xs font-normal mr-1">
                ریال
              </span>
            </div>
          </div>

        </div>

        {/* Result Count */}
        <div className="mt-4 text-sm text-gray-500">
          تعداد نتایج:

          <span className="font-semibold text-gray-700 mr-1">
            {new Intl.NumberFormat("fa-IR").format(totalItems)}
          </span>
        </div>

      </div>

      {/* Table */}
      {currentLogs.length === 0 ? (

        <div className="p-8 text-center text-gray-500">
          گزارشی برای نمایش وجود ندارد.
        </div>

      ) : (

        <div className="overflow-x-auto">

          <table className="w-full text-sm text-center">

            <thead className="bg-gray-50">

              <tr>

                <th className="px-4 py-3 whitespace-nowrap">
                  توضیحات
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  نام کاربری
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  مبلغ تراکنش
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  مبلغ کارمزد
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  مالیات
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  مبلغ خالص
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  نوع
                </th>

                <th className="px-4 py-3 whitespace-nowrap">
                  تاریخ
                </th>

              </tr>

            </thead>

            <tbody>

              {currentLogs.map((item) => (

                <tr
                  key={item.id}
                  className="border-t text-center hover:bg-gray-50"
                >

                  <td className="px-4 py-3">
                    {item.description || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {item.username || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {formatAmount(item.amount)}
                  </td>

                  <td className="px-4 py-3 font-semibold text-green-600">
                    {formatAmount(item.commissionAmount)}
                  </td>

                  <td className="px-4 py-3">
                    {formatAmount(item.taxAmount)}
                  </td>

                  <td className="px-4 py-3">
                    {formatAmount(item.netAmount)}
                  </td>

                  <td className="px-4 py-3">
                    {getTypeLabel(item.type)}
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDate(item.createdAt)}
                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex md:flex-row items-center justify-between gap-4 p-4 border-t bg-gray-50">

          {/* Items per page */}
          <div className="flex items-center gap-2 text-sm text-gray-600">

            <span>
              نمایش در هر صفحه:
            </span>

            <select
              value={itemsPerPage}
              onChange={(e) =>
                setItemsPerPage(Number(e.target.value))
              }
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 outline-none"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

          </div>

          {/* Showing */}
          <div className="text-sm text-gray-500">

            نمایش

            <span className="font-semibold mx-1">
              {new Intl.NumberFormat("fa-IR").format(
                startIndex + 1
              )}
            </span>

            تا

            <span className="font-semibold mx-1">
              {new Intl.NumberFormat("fa-IR").format(
                Math.min(endIndex, totalItems)
              )}
            </span>

            از

            <span className="font-semibold mx-1">
              {new Intl.NumberFormat("fa-IR").format(
                totalItems
              )}
            </span>

          </div>

          {/* Pagination buttons */}
          <div className="flex items-center gap-1">

            {/* Previous */}
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => changePage(currentPage - 1)}
              className={`px-3 py-2 rounded-lg border text-sm transition ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
            >
              قبلی
            </button>

            {/* Page Numbers */}
            {getPageNumbers().map((page, index) => {

              if (page === "...") {
                return (
                  <span
                    key={`dots-${index}`}
                    className="px-2 text-gray-500"
                  >
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => changePage(page)}
                  className={`min-w-[38px] px-3 py-2 rounded-lg border text-sm transition ${currentPage === page
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                >
                  {new Intl.NumberFormat("fa-IR").format(page)}
                </button>
              );
            })}

            {/* Next */}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => changePage(currentPage + 1)}
              className={`px-3 py-2 rounded-lg border text-sm transition ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
            >
              بعدی
            </button>

          </div>

        </div>
      )}

    </div>
  );
}